<script setup lang="ts">
    import { ArrowRight, Check, HelpCircle, X } from 'lucide-vue-next';
    import { Button } from '@/components/ui/button';
    import { computed, ref, watch } from 'vue';
    import { InputRow } from '@/components/ui/form';
    import { Textarea } from '@/components/ui/textarea';
    import { useForm } from '@inertiajs/vue3';
    import { useI18n } from 'vue-i18n';
    import { usePage } from '@inertiajs/vue3';
    import axios from 'axios';
    import FileUploadZone from '@/components/FileUploadZone.vue';
    import OnboardingStep from './OnboardingStep.vue';
    import type { FileItem, OnboardingStep as OnboardingStepType } from '@/types';
    import type { SharedData, CompanyOnboardingFile } from '@/types';
    import OnboardingHelpVideo from './OnboardingHelpVideo.vue';

    const SKAT_LINK =
        'https://pdcs.skat.dk/dcs-atn-gateway/nemlogin?targetUrl=aHR0cHM6Ly9udHNlLnNrYXQuZGsvbnRzZS1mcm9udC9jb250ZW50P2lkPWZyYW1lOjJGQUF1dG9yaXNlcnJldmlzb3JtZmw=';

    const { t, tm } = useI18n();

    const props = defineProps<{
        onboardingSteps: OnboardingStepType[];
        modelValue?: boolean;
        companyId: number;
    }>();

    const emit = defineEmits<{
        'update:modelValue': [value: boolean];
        close: [];
        completed: [incompleteSteps: number];
    }>();

    const open = computed({
        get: () => props.modelValue ?? false,
        set: (value) => emit('update:modelValue', value),
    });

    const activeStepIndex = ref(0);

    type FormState = OnboardingStepType & {
        status: 'completed' | 'rejected' | 'pending';
        selectedAcceptIndex?: string;
        comment?: string;
        files?: FileItem[];
    };

    const formStates = ref<Record<number, FormState>>({});

    const completedSteps = computed(() =>
        props.onboardingSteps.filter((step) => step.is_completed).sort((a, b) => a.order - b.order),
    );

    const incompleteSteps = computed(() =>
        props.onboardingSteps
            .filter((step) => !step.is_completed)
            .sort((a, b) => a.order - b.order),
    );

    const activeStep = computed(() => incompleteSteps.value[activeStepIndex.value] || null);

    const isLastStep = computed(() => activeStepIndex.value === incompleteSteps.value.length - 1);

    const canProceed = computed(() => {
        if (!activeStep.value) return true; // Allow proceeding if there's no active step (all completed)
        const formState = formStates.value[activeStep.value.id];
        return formState?.status === 'completed' || formState?.status === 'rejected';
    });

    const form = useForm({
        steps: [],
    });

    const goToNextStep = () => {
        if (isLastStep.value) {
            form.transform((data) => ({
                ...data,
                steps: Object.entries(formStates.value).map(([id, state]) => ({
                    id: parseInt(id),
                    status: state.status,
                    comment: state.comment,
                    files: state.files,
                })),
            }));
            form.post(route('v2.onboarding.customer.complete', { company: props.companyId }), {
                forceFormData: true,
                onSuccess: () => {
                    emit('completed', incompleteSteps.value.length);
                },
                onError: (error: any) => {},
            });
            return;
        }
        activeStepIndex.value++;
        setFormValues();
    };

    const resetForm = () => {
        formStates.value = {};
        activeStepIndex.value = 0;
    };

    const goToPreviousStep = () => {
        if (activeStepIndex.value > 0) {
            activeStepIndex.value--;
        }
    };

    const close = () => {
        resetForm();
        emit('close');
    };

    // Modify the updateStepStatus function to track which accept button was clicked
    const updateStepStatus = (status: 'completed' | 'rejected', acceptIndex?: string) => {
        if (!activeStep.value) return;

        formStates.value = {
            ...formStates.value,
            [activeStep.value.id]: {
                ...(formStates.value[activeStep.value.id] ?? {}),
                status,
                selectedAcceptIndex: status === 'completed' ? acceptIndex : undefined,
            },
        };
    };

    const setFormValues = () => {
        formStates.value = {
            ...formStates.value,
            [activeStep.value.id]: {
                ...(formStates.value[activeStep.value.id] ?? {}),
                comment: activeStep.value.comment,
                files: activeStep.value.existing_files?.map((file: CompanyOnboardingFile) => ({
                    id: file.id,
                    name: file.original_filename,
                    url: route('v2.onboarding.files.download', {
                        companyOnboardingStep: activeStep.value.id,
                        storageFile: file.id,
                    }),
                    size: 0,
                    uploading: false,
                    uploaded: true,
                    progress: 0,
                    registered: true,
                    mime: file.mime,
                })),
            },
        };
    };

    watch(
        open,
        (newValue) => {
            if (!newValue) return;
            if (activeStep.value && !formStates.value[activeStep.value.id]) {
                setFormValues();
            }
        },
        {
            immediate: true,
        },
    );

    const stepImage = (step: OnboardingStepType) => {
        if (step.service === 'e-conomic') {
            return '/img/onboarding/economic.png';
        }
        if (step.service === 'vat' || step.service === 'e-income') {
            return '/img/onboarding/skat.png';
        }
    };

    const videoLink = (step: OnboardingStepType) => {
        return 'https://cdn.plyr.io/static/demo/View_From_A_Blue_Moon_Trailer-720p.mp4';
    };

    const openLink = (link: string | undefined) => {
        if (!link) return;
        window.open(link, '_blank');
    };

    const handleFileDelete = async ({ id }: { id: number }) => {
        console.log(id);
        await axios.delete(
            route('v2.onboarding.files.delete', {
                companyOnboardingStep: activeStep.value.id,
                storageFile: id,
            }),
        );
    };
</script>

<template>
    <div class="mb-10 flex w-full gap-2 sm:mt-12 sm:mb-20">
        <TransitionGroup
            name="onboarding-step"
            tag="div"
            class="flex w-full gap-2"
        >
            <!-- Incomplete Steps -->
            <OnboardingStep
                v-for="step in incompleteSteps"
                :key="step.id"
                :step="step"
                :form-state="formStates[step.id]"
                :separator="step !== incompleteSteps[incompleteSteps.length - 1]"
            />

            <!-- Completed Steps -->
            <OnboardingStep
                v-for="step in completedSteps"
                :key="step.id"
                :step="step"
                :form-state="formStates[step.id]"
                :separator="step !== completedSteps[completedSteps.length - 1]"
            />
        </TransitionGroup>
    </div>

    <TransitionGroup
        name="onboarding-content"
        tag="div"
        class="relative px-2 sm:px-5"
    >
        <div
            v-if="activeStep"
            :key="activeStep.id"
            class="w-full"
        >
            <div class="mb-10 grid gap-4 sm:grid-cols-3">
                <div class="col-span-2 flex flex-col gap-2 sm:gap-4">
                    <h3 class="text-secondary text-lg font-semibold sm:text-2xl">
                        {{ activeStep.title }}
                    </h3>

                    <p class="text-muted-foreground text-xs sm:text-sm">
                        {{
                            activeStep.description ??
                            t('onboarding.steps.' + activeStep.service + '.description')
                        }}
                    </p>
                </div>
                <div class="order-first col-span-1 sm:order-last">
                    <img
                        v-if="stepImage(activeStep)"
                        :src="stepImage(activeStep)"
                        alt="Onboarding Step Image"
                        class="ml-auto max-w-6 sm:max-w-full"
                    />
                </div>
            </div>

            <div class="flex flex-col gap-4">
                <InputRow
                    v-if="activeStep.allow_comments && formStates[activeStep.id]"
                    :form="form"
                    name="comment"
                    label="onboarding.comment"
                >
                    <Textarea
                        v-model="formStates[activeStep.id].comment"
                        name="comment"
                        :placeholder="t('onboarding.comment')"
                    />
                </InputRow>

                <InputRow
                    v-if="activeStep.allow_files && formStates[activeStep.id]"
                    :form="form"
                    name="files"
                    label="onboarding.files"
                >
                    <FileUploadZone
                        v-model="formStates[activeStep.id].files"
                        multiple
                        :max-uploads="3"
                        :delete-file="handleFileDelete"
                        :accepts-files="[
                            '.pdf',
                            '.jpg',
                            '.jpeg',
                            '.png',
                            '.doc',
                            '.docx',
                            '.xls',
                            '.xlsx',
                            '.ppt',
                            '.pptx',
                            '.csv',
                        ]"
                    />
                </InputRow>

                <Button
                    v-for="(accept, index) in tm(
                        'onboarding.steps.' + activeStep.service + '.accept',
                    )"
                    :key="index"
                    type="button"
                    variant="selector"
                    size="xl"
                    width="full"
                    :class="{
                        'border-foreground [&_svg]:text-primary':
                            formStates[activeStep.id]?.status === 'completed' &&
                            formStates[activeStep.id]?.selectedAcceptIndex === index,
                    }"
                    @click="updateStepStatus('completed', index)"
                >
                    <Check />
                    {{ accept }}
                </Button>

                <Button
                    type="button"
                    variant="selector"
                    size="xl"
                    width="full"
                    :class="{
                        'border-foreground [&_svg]:text-destructive':
                            formStates[activeStep.id]?.status === 'rejected',
                    }"
                    @click="updateStepStatus('rejected')"
                >
                    <X /> {{ t('onboarding.steps.' + activeStep.service + '.reject') }}
                </Button>
                <Button
                    v-if="activeStep.service === 'vat'"
                    type="button"
                    variant="selector"
                    size="xl"
                    width="full"
                    class="justify-center border-transparent shadow-none hover:[&_svg]:-rotate-45"
                    @click="openLink(SKAT_LINK)"
                >
                    {{ t('onboarding.steps.' + activeStep.service + '.give_access') }}
                    <ArrowRight class="transition-transform duration-100" />
                </Button>
            </div>
        </div>
        <div
            v-if="!incompleteSteps.length"
            key="completed"
            class="flex w-full flex-col items-center gap-5 text-center"
        >
            <h3 class="text-secondary text-2xl font-medium">
                {{ t('onboarding.steps_completed.title') }}
            </h3>
            <p class="text-muted-foreground text-sm">
                {{ t('onboarding.assistant.completed') }}
            </p>
        </div>
    </TransitionGroup>

    <div class="mt-auto grid grid-cols-2 gap-2 pt-20 sm:grid-cols-3">
        <div>
            <!-- <OnboardingHelpVideo :video-src="videoLink(activeStep)" /> -->
        </div>
        <div class="flex justify-end gap-2 sm:justify-center">
            <Button
                v-if="!incompleteSteps.length"
                type="button"
                variant="outline"
                :disabled="form.processing"
                @click="close"
            >
                {{ t('onboarding.close') }}
            </Button>
            <Button
                v-if="activeStepIndex > 0 && incompleteSteps.length"
                type="button"
                variant="outline"
                :disabled="form.processing"
                @click="goToPreviousStep"
            >
                {{ t('onboarding.back') }}
            </Button>

            <Button
                v-if="incompleteSteps.length"
                type="button"
                :disabled="!canProceed || form.processing"
                @click.prevent="goToNextStep"
                :loading="form.processing"
            >
                {{ isLastStep ? t('onboarding.complete') : t('onboarding.next') }}
            </Button>
        </div>
    </div>
</template>

<style scoped>
    /* Step transitions */
    .onboarding-step-move,
    .onboarding-step-enter-active,
    .onboarding-step-leave-active {
        transition: all 0.1s ease-in-out;
    }

    .onboarding-step-enter-from,
    .onboarding-step-leave-to {
        opacity: 0;
    }

    .onboarding-step-leave-active {
        position: absolute;
    }

    /* Content transitions */
    .onboarding-content-move,
    .onboarding-content-enter-active {
        transition: all 0.1s ease-in-out;
    }

    .onboarding-content-enter-from {
        opacity: 0;
    }

    .onboarding-content-leave-to {
        opacity: 0;
    }

    .onboarding-content-leave-active {
        position: absolute;
        width: 100%;
    }
</style>
